import * as UI from "@iyk/ui"
import * as React from "react"
import { GlobalRoutes } from "../../../react-router/routes.ts"
import { formatPrice } from "../../ui/format-price.ts"
import { Link } from "../../ui/link.tsx"
import type { Shop } from "../shop.ts"
import AddToCartButton from "./add-to-cart-button.tsx"

export const AddToCartSelector = ({ selectedProduct, products }: AddToCartSelector.Props) => {
  const [selectedVariant, setSelectedVariant] = React.useState(selectedProduct.variants[0])

  const [quantity, setQuantity] = React.useState(1)

  const handleQuantityChange = (value: number) => {
    setQuantity((prev) => {
      const newQuantity = prev + value
      if (newQuantity < 1) return prev
      return newQuantity
    })
  }

  const resetQuantity = () => {
    setQuantity(1)
  }

  const hasMoreThanOneVariant = selectedProduct.variants.length > 1

  return (
    <div className="grid gap-6 pt-6">
      <div className="w-full aspect-square bg-[rgba(66,66,66,0.20)] overflow-hidden">
        <img
          src={selectedProduct.imageUrl}
          alt={selectedProduct.title}
          className="size-full object-contain"
        />
      </div>

      <div>
        <h2 className="text-xl">{selectedProduct.title}</h2>
        <p className="text-base-title">{formatPrice(selectedVariant.price)}</p>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1.5 hide-scrollbar snap-x snap-mandatory cursor-grab">
        {products.map((product) => (
          <Link
            key={product.id}
            to={GlobalRoutes.shop.productDetail(product.id)}
            onClick={() => {
              setSelectedVariant(product.variants[0])
              resetQuantity()
            }}
            data-selected={selectedProduct.id === product.id}
            className={UI.cx(
              "size-[50px] flex-shrink-0 bg-[rgba(66,66,66,0.20)] relative",
              "after:absolute after:content-[''] after:w-full after:h-[1px] after:-bottom-1.5 after:left-0",
              "after:transition-all after:duration-300",
              "data-[selected=true]:after:bshadow-b after:bshadow-gray-12"
            )}
          >
            <img src={product.imageUrl} className="size-full object-contain select-none" draggable={false} />
          </Link>
        ))}
      </div>

      {hasMoreThanOneVariant && (
        <UI.Select
          className="w-full"
          name="variant"
          defaultValue={selectedVariant.id}
          onChange={(e) => setSelectedVariant(selectedProduct.variants.find((v) => v.id === e.target.value)!)}
        >
          {selectedProduct.variants.map((variant) => (
            <option key={variant.id} value={variant.id}>
              {variant.title}
            </option>
          ))}
        </UI.Select>
      )}

      <div className="flex items-center gap-3">
        <button
          className="h-5 w-4 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => handleQuantityChange(-1)}
          disabled={quantity === 1}
        >
          —
        </button>
        <span className="text-sm size-5 text-center">{quantity}</span>
        <button className="h-5 w-4 text-sm" onClick={() => handleQuantityChange(1)}>
          +
        </button>
      </div>

      <div className="grid gap-2">
        <AddToCartButton
          productVariantId={selectedVariant.id}
          quantity={quantity}
          name={selectedProduct.title}
          variant={hasMoreThanOneVariant ? selectedVariant.title : undefined}
        />
        <Link to={GlobalRoutes.shop.cart} className={UI.classForButton({ size: "md", kind: "shaded" })}>
          View my Cart
        </Link>
      </div>

      <p className="text-sm">{selectedProduct.description}</p>
    </div>
  )
}

namespace AddToCartSelector {
  export interface Props {
    selectedProduct: Shop.Product
    products: Shop.Product[]
  }
}
